import { BiCardQuery } from '@euler/model/bi/blend';
import { BiDashboardFilters } from '@euler/model/bi/dashboard';
import { BiVizSpec } from '@euler/model/bi/viz';
import { useEffect, useMemo, useState } from 'react';
import { biStyleProblem } from '../style/functions/biStyleProblem';
import {
  BiEditorState,
  BiEditorTarget,
  biEditorProblem,
  biEditorSpec,
  biEditorStateFromCard,
  biEditorStateNew,
  biEditorViz,
} from './biEditorState';
import { useBiCatalogs } from './useBiCatalogs';
import { useBiDatasetCatalog } from './useBiDatasetCatalog';
import { BiPreviewKnown, biPreviewKey, useBiPreview } from './useBiPreview';

export type BiCardSave = (input: {
  spec: BiCardQuery;
  viz: BiVizSpec;
  title: string;
}) => Promise<{ ok: true } | { ok: false; message: string }>;

/**
 * The card form's state, from the card (or empty) each time a target is
 * given: catalogs, the spec and viz it would save, the live preview, and
 * the save itself. A refusal keeps the form and says why.
 */
export function useBiCardEditor(
  /** Null while no form is open. */
  target: BiEditorTarget | null,
  onSave: BiCardSave,
  /** The filters the card is viewed under; the preview runs under them. */
  filters: BiDashboardFilters,
) {
  const visible = target !== null;
  const catalogs = useBiCatalogs(visible);
  const [state, setState] = useState<BiEditorState>(biEditorStateNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!target) return;
    setError(null);
    setState(
      target.kind === 'edit'
        ? biEditorStateFromCard(target.card)
        : biEditorStateNew(),
    );
  }, [target]);

  const catalog = useBiDatasetCatalog(state.dataset, catalogs.state);
  const original = target?.kind === 'edit' ? target.card.viz : undefined;
  const problem = biEditorProblem(state);
  const spec = useMemo(
    () => (problem ? null : biEditorSpec(state)),
    [problem, state],
  );
  const viz = useMemo(() => biEditorViz(state, original), [state, original]);
  // What the card showed when the form opened, for the spec the form
  // builds from the card untouched (not the stored one, whose shape the
  // form may normalize).
  const known = useMemo<BiPreviewKnown | null>(() => {
    if (target?.kind !== 'edit' || !target.loaded) return null;
    const untouched = biEditorStateFromCard(target.card);
    const saved = biEditorProblem(untouched) ? null : biEditorSpec(untouched);
    if (!saved) return null;
    return {
      key: biPreviewKey(saved, target.loaded.filters),
      result: target.loaded.result,
    };
  }, [target]);
  const preview = useBiPreview(visible ? spec : null, filters, known);
  const title = state.title.trim();
  const styleProblem = biStyleProblem(viz?.style);
  const canSave =
    catalogs.state.kind === 'ready' &&
    !!spec &&
    !!viz &&
    !!title &&
    !styleProblem;

  /** True once saved. */
  const save = async (): Promise<boolean> => {
    if (!canSave || !spec || !viz) return false;
    setBusy(true);
    setError(null);
    const saved = await onSave({ spec, viz, title });
    setBusy(false);
    if (!saved.ok) setError(saved.message);
    return saved.ok;
  };

  return {
    state,
    setState,
    catalogs,
    catalog,
    problem,
    spec,
    viz,
    preview,
    busy,
    error,
    canSave,
    save,
  };
}

export type BiCardEditor = ReturnType<typeof useBiCardEditor>;
